import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, KeyRound, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PropertyAccessInstructions } from "@/hooks/useProperties";

const ENTRY_METHOD_LABELS: Record<string, string> = {
  lockbox: "Lockbox",
  keypad: "Keypad / Code",
  key: "Physical key",
  smart_lock: "Smart lock",
  concierge: "Concierge / Front desk",
  other: "Other",
};

interface AccessInstructionsViewProps {
  instructions: PropertyAccessInstructions | null;
  /** Default open when there is content */
  defaultOpen?: boolean;
  className?: string;
}

export function AccessInstructionsView({
  instructions,
  defaultOpen = true,
  className,
}: AccessInstructionsViewProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const hasAny = !!(
    instructions?.entry_method ??
    instructions?.entry_code ??
    instructions?.entry_location ??
    instructions?.unit_location ??
    instructions?.parking_instructions ??
    instructions?.supply_closet_info ??
    instructions?.access_notes
  );

  const copyCode = () => {
    if (!instructions?.entry_code) return;
    navigator.clipboard.writeText(instructions.entry_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("rounded-lg border border-border bg-muted/30", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between h-auto py-3 px-4 hover:bg-muted/50"
        >
          <span className="flex items-center gap-2 font-medium">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            Access Instructions
          </span>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 pt-0 space-y-3 text-sm">
          {!hasAny ? (
            <p className="text-muted-foreground">No access instructions added yet.</p>
          ) : (
            <>
              {instructions?.entry_method && (
                <p>
                  <span className="text-muted-foreground">Entry: </span>
                  {ENTRY_METHOD_LABELS[instructions.entry_method] ?? instructions.entry_method}
                </p>
              )}
              {instructions?.unit_location && (
                <p>
                  <span className="text-muted-foreground">Unit: </span>
                  {instructions.unit_location}
                </p>
              )}
              {instructions?.entry_location && (
                <p>
                  <span className="text-muted-foreground">Where to find it: </span>
                  {instructions.entry_location}
                </p>
              )}
              {instructions?.entry_code && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Code: </span>
                  <code className="flex-1 rounded bg-background px-2 py-1 font-mono text-xs">
                    {instructions.entry_code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-8"
                    onClick={copyCode}
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              )}
              {instructions?.parking_instructions && (
                <p>
                  <span className="text-muted-foreground">Parking: </span>
                  {instructions.parking_instructions}
                </p>
              )}
              {instructions?.supply_closet_info && (
                <p>
                  <span className="text-muted-foreground">Supplies: </span>
                  {instructions.supply_closet_info}
                </p>
              )}
              {instructions?.access_notes && (
                <p className="whitespace-pre-wrap">
                  <span className="text-muted-foreground">Notes: </span>
                  {instructions.access_notes}
                </p>
              )}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
